import { AlertTriangle } from 'lucide-react';
import { useCredits } from '../../hooks/useCredits';
import './ExpiryWarningBanner.css';

interface ExpiryWarningBannerProps {
  /** true の場合、コンパクト表示（ヘッダー用） */
  compact?: boolean;
}

/**
 * 期限付きポイントの失効警告バナー
 * 7日以内に失効するポイントがある場合に表示
 */
export function ExpiryWarningBanner({ compact = false }: ExpiryWarningBannerProps) {
  const { data: credits } = useCredits();

  // 失効予定がない場合は表示しない
  if (!credits?.expiring_soon || credits.expiring_soon.amount <= 0) {
    return null;
  }

  const { amount, expires_at, days_until_expiry } = credits.expiring_soon;
  const expiryDate = new Date(expires_at);
  const isUrgent = days_until_expiry <= 3;

  const formatExpiryDate = () => {
    return expiryDate.toLocaleDateString('ja-JP', {
      month: 'numeric',
      day: 'numeric',
    });
  };

  const getMessage = () => {
    if (days_until_expiry === 0) {
      return `本日中に ${Math.floor(amount).toLocaleString()} pt が失効します`;
    } else if (days_until_expiry === 1) {
      return `明日 ${Math.floor(amount).toLocaleString()} pt が失効します`;
    } else {
      return `${formatExpiryDate()} までに ${Math.floor(amount).toLocaleString()} pt が失効`;
    }
  };

  if (compact) {
    return (
      <div className={`expiry-warning-compact ${isUrgent ? 'expiry-warning-urgent' : ''}`}>
        <span className="expiry-warning-icon">
          <AlertTriangle size={14} aria-hidden="true" />
        </span>
        <span className="expiry-warning-text">{getMessage()}</span>
      </div>
    );
  }

  return (
    <div className={`expiry-warning-banner ${isUrgent ? 'expiry-warning-urgent' : ''}`}>
      <div className="expiry-warning-content">
        <span className="expiry-warning-icon">
          <AlertTriangle size={18} aria-hidden="true" />
        </span>
        <div className="expiry-warning-message">
          <strong>ポイント失効のお知らせ</strong>
          <p>
            {days_until_expiry === 0 ? (
              <>本日中に <strong>{Math.floor(amount).toLocaleString()} ポイント</strong> が失効します。</>
            ) : days_until_expiry === 1 ? (
              <>明日（{formatExpiryDate()}）に <strong>{Math.floor(amount).toLocaleString()} ポイント</strong> が失効します。</>
            ) : (
              <>{formatExpiryDate()} までに <strong>{Math.floor(amount).toLocaleString()} ポイント</strong> が失効します。あと <strong>{days_until_expiry}日</strong> です。</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
